import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Avatar,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  Brand,
  useGetBrandModels,
  useCreateBrandModel,
  useUpdateBrandModel,
  useDeleteBrandModel,
} from "../../api/brand";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import useStyles from "./styles";

interface Props {
  open: boolean;
  brand: Brand;
  onClose: () => void;
}

export const ModalBrandModels: React.FC<Props> = ({ open, brand, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { models, isPending } = useGetBrandModels(brand.id, open && brand.id > 0);
  const createModel = useCreateBrandModel(brand.id);
  const updateModel = useUpdateBrandModel(brand.id);
  const deleteModel = useDeleteBrandModel(brand.id);

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) {
      toast.error(t("brand.modelNameRequired", "Le nom du modèle est requis"));
      return;
    }
    createModel.mutate(name, {
      onSuccess: () => {
        setNewName("");
        toast.success(t("brand.modelAdded", "Modèle ajouté"));
      },
    });
  };

  const startEdit = (id: number, name: string) => {
    setEditingId(id);
    setEditingName(name);
  };

  const saveEdit = (id: number) => {
    const name = editingName.trim();
    if (!name) {
      toast.error(t("brand.modelNameRequired", "Le nom du modèle est requis"));
      return;
    }
    updateModel.mutate(
      { id, name },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditingName("");
          toast.success(t("brand.modelUpdated", "Modèle mis à jour"));
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    deleteModel.mutate(id, {
      onSuccess: () => toast.success(t("brand.modelDeleted", "Modèle supprimé")),
    });
  };

  const busy =
    createModel.isPending || updateModel.isPending || deleteModel.isPending;

  return (
    <>
      {busy && <CustomLoader isActive={busy} />}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={brand.logo_url || undefined}
              variant="rounded"
              sx={{ width: 36, height: 36, bgcolor: colors.bgcolor }}
            >
              {!brand.logo_url && (brand.name?.[0] || "B")}
            </Avatar>
            <Typography variant="h6" component="span" fontWeight={600}>
              {t("brand.modelsOf", "Modèles")} — {brand.name}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Add a model */}
          <Box className={classes.addModelRow} mt={1}>
            <TextField
              fullWidth
              size="small"
              label={t("brand.newModel", "Nouveau modèle")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              placeholder="Clio, Megane, ..."
            />
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={busy || !newName.trim()}
              startIcon={<Icon name="plus" width="16px" height="16px" />}
            >
              {t("common.add", "Ajouter")}
            </Button>
          </Box>

          {/* List */}
          {isPending ? (
            <Typography variant="body2" color="text.secondary">
              {t("common.loading", "Chargement...")}
            </Typography>
          ) : models.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("brand.noModels", "Aucun modèle pour cette marque.")}
            </Typography>
          ) : (
            <Box>
              {models.map((m) => (
                <Box key={m.id} className={classes.modelRow}>
                  {editingId === m.id ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(m.id);
                        }}
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => saveEdit(m.id)}
                        disabled={busy}
                      >
                        <Icon name="check" width="18px" height="18px" color={colors.primary} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setEditingId(null)}>
                        <Icon name="close" width="18px" height="18px" color={colors.greydark} />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <span className={classes.modelName}>{m.name}</span>
                      <IconButton
                        size="small"
                        onClick={() => startEdit(m.id, m.name)}
                      >
                        <Icon name="pencil" width="16px" height="16px" color={colors.greydark} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(m.id)}
                        disabled={busy}
                      >
                        <Icon name="trash" width="16px" height="16px" color={colors.error} />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            {t("common.close", "Fermer")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
